/* useSectionNav — active-section tracking + page scroll progress for the
   sticky pill nav on every preview / detail page.
   • IntersectionObserver (no scroll polling for the active pill).
   • rAF-throttled scroll listener for the 0..1 progress rail.
   • Locks the observer briefly after a manual pill tap so the smooth-scroll
     doesn't cause the active pill to flicker between intermediate sections.
   Safe on low-end phones — no work outside a single rAF per scroll frame. */
import { useCallback, useEffect, useRef, useState } from 'react';

/** rootMargin keeps the "active" pill stable: the section is considered
   active while its top is between just below the sticky nav (top offset)
   and the middle of the viewport. Prevents flicker near boundaries. */
const DEFAULT_TOP_OFFSET = 120; // px — comfortable clearance for sticky nav
const CLICK_LOCK_MS      = 1000;

export function useSectionNav(sectionIds, { topOffset = DEFAULT_TOP_OFFSET } = {}) {
  const idsKey = Array.isArray(sectionIds) ? sectionIds.join('|') : '';
  const [activeId, setActiveId] = useState(sectionIds?.[0] || null);
  const [progress, setProgress] = useState(0);
  const lockUntilRef = useRef(0);

  // ── Active section via IntersectionObserver ──
  useEffect(() => {
    if (!sectionIds || sectionIds.length === 0) return undefined;
    const els = sectionIds.map((id) => document.getElementById(id)).filter(Boolean);
    if (els.length === 0) return undefined;

    const rootMargin = `-${topOffset}px 0px -55% 0px`;
    const visible = new Map(); // id → intersectionRatio

    const obs = new IntersectionObserver(
      (entries) => {
        if (Date.now() < lockUntilRef.current) return;
        entries.forEach((e) => {
          if (e.isIntersecting) visible.set(e.target.id, e.intersectionRatio);
          else visible.delete(e.target.id);
        });
        if (visible.size === 0) return;
        // Pick the visible section whose top sits closest to the sticky nav
        // edge, so a freshly targeted section beats a long neighbour above it.
        let best = null, bestDist = Infinity;
        visible.forEach((_, id) => {
          const top = document.getElementById(id)?.getBoundingClientRect().top ?? Infinity;
          const dist = top >= topOffset ? top - topOffset : (topOffset - top) * 1.5;
          if (dist < bestDist) { bestDist = dist; best = id; }
        });
        if (best) setActiveId(best);
      },
      { rootMargin, threshold: [0, 0.15, 0.5, 1] },
    );
    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsKey, topOffset]);

  // ── Page scroll progress (0..1) via rAF-throttled scroll listener ──
  useEffect(() => {
    let raf = 0;
    const update = () => {
      const de = document.documentElement;
      const max = Math.max(1, de.scrollHeight - window.innerHeight);
      const p = Math.min(1, Math.max(0, window.scrollY / max));
      setProgress(p);
      raf = 0;
    };
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(update); };
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  const scrollToId = useCallback((id) => {
    const el = document.getElementById(id);
    if (!el) return;
    // Lock the observer briefly so the smooth-scroll traversal doesn't
    // flicker the active pill through intermediate sections.
    lockUntilRef.current = Date.now() + CLICK_LOCK_MS;
    setActiveId(id);
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  return { activeId, progress, scrollToId };
}
